import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Loader2, Microscope, Share2, Sparkles, TrendingUp } from "lucide-react";
import { api, formatError } from "../lib/api";

export function AutopsyWallListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/autopsy/wall?limit=50");
        setItems(data.autopsies || []);
      } catch (_e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50" data-testid="autopsy-wall-page">
      <header className="border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white/95 dark:bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-black text-lg"><span className="text-brand">⚡</span> CopyForge</Link>
          <Link to="/try" className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 text-sm font-bold">
            Dissect your post →
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-zinc-100 dark:bg-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest mb-4">
            <Microscope className="w-3 h-3" /> Featured Wall · Top 50
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3">
            Why these posts went viral.
          </h1>
          <p className="text-lg text-zinc-600 dark:text-zinc-400">
            Real autopsies of real viral content — dissected by CopyForge AI. Steal the hooks, study the structure, ship better content.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="border-2 border-dashed border-zinc-300 dark:border-zinc-700 p-12 text-center">
            <Microscope className="w-10 h-10 mx-auto mb-3 text-zinc-400" />
            <p className="text-zinc-500 mb-1">No public autopsies yet.</p>
            <Link to="/try" className="text-brand underline">Be the first — dissect a post and publish it →</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((d) => (
              <Link
                key={d.id}
                to={`/wall/${d.id}`}
                data-testid={`wall-card-${d.id}`}
                className="block border-2 border-zinc-900 dark:border-zinc-100 p-5 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition"
              >
                <div className="flex items-center justify-between mb-2 text-xs text-zinc-500">
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{d.view_count || 0}</span>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{d.share_count || 0} shares</span>
                </div>
                <h3 className="font-bold text-lg mb-2 line-clamp-2">{d.title}</h3>
                {d.autopsy?.hook_type && (
                  <div className="text-xs text-zinc-600 dark:text-zinc-400">
                    Hook type: <span className="font-bold">{d.autopsy.hook_type}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export function AutopsyWallDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/autopsy/wall/${id}`);
        setDoc(data);
      } catch (e) {
        toast.error(formatError(e?.response?.data?.detail) || "Not found");
        nav("/wall");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: doc?.title, url }); } catch (_e) { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    }
    api.post(`/public/autopsy/wall/${id}/share`).catch(() => {});
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!doc) return null;
  const a = doc.autopsy || {};

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50">
      <header className="border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white/95 dark:bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/wall" className="text-sm hover:underline">← Wall</Link>
          <button onClick={share} data-testid="wall-share-btn" className="inline-flex items-center gap-1 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 text-sm font-bold">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 space-y-6" data-testid="wall-detail">
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter">{doc.title}</h1>

        <section className="border-l-4 border-zinc-900 dark:border-zinc-100 pl-5 py-2">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Original post</h3>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{doc.source_excerpt}</p>
        </section>

        {a.hook_type && (
          <section className="border-2 border-zinc-900 dark:border-zinc-100 p-5">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Hook type</h3>
            <p className="font-bold text-lg">{a.hook_type}</p>
          </section>
        )}

        {a.psychological_trigger && (
          <section className="border-2 border-zinc-900 dark:border-zinc-100 p-5">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Psychological trigger</h3>
            <p className="text-sm">{a.psychological_trigger}</p>
          </section>
        )}

        {a.structural_template && (
          <section className="border-2 border-zinc-900 dark:border-zinc-100 p-5">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Structural template</h3>
            <p className="whitespace-pre-wrap text-sm">{a.structural_template}</p>
          </section>
        )}

        {a.your_version && (
          <section className="border-2 border-green-600 dark:border-green-500 p-5 bg-green-50/30 dark:bg-green-950/20">
            <h3 className="text-xs uppercase tracking-widest text-green-700 dark:text-green-400 mb-1">Your version</h3>
            <p className="whitespace-pre-wrap text-sm">{a.your_version}</p>
          </section>
        )}

        <section className="border-2 border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 p-8 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-3" />
          <h3 className="text-2xl font-black mb-2">Dissect YOUR post for free.</h3>
          <p className="opacity-80 mb-4">No signup. Paste any viral text. Get the same autopsy in 10 seconds.</p>
          <Link to="/try" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white px-6 py-3 font-bold inline-block">
            Try the free dissector →
          </Link>
        </section>
      </main>
    </div>
  );
}
